import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Swords,
  Play,
  Trophy,
  Target,
  TrendingUp,
  TrendingDown,
  Clock,
  ChevronRight,
  Loader2,
  UserCircle,
  Shield,
  Zap,
  Flame,
  Snowflake,
  Smile,
  Mic,
  MessageSquare,
  Brain,
  Award,
  Star,
  Crown,
  BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERSONA_ICONS = {
  budget_conscious: Snowflake,
  busy_decision_maker: Zap,
  technical_buyer: Shield,
  skeptical_buyer: Flame,
  friendly_noncommittal: Smile
};

const DIFFICULTY_COLORS = {
  easy: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  medium: 'bg-yellow-500/15 text-amber-600 border-yellow-500/30',
  hard: 'bg-red-500/15 text-red-600 border-red-500/30'
};

export default function Practice() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [difficulties, setDifficulties] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState('medium');
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [achievements, setAchievements] = useState(null);

  useEffect(() => {
    Promise.all([fetchPersonas(), fetchSessions(), fetchStats(), fetchRecommendations(), fetchAchievements()])
      .finally(() => setLoading(false));
  }, []);

  const fetchPersonas = async () => {
    try {
      const res = await fetch(`${API_URL}/api/roleplay/personas`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPersonas(data.personas);
        setDifficulties(data.difficulties);
      }
    } catch (e) { console.error(e); }
  };

  const fetchSessions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/roleplay/sessions`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSessions(data.sessions);
      }
    } catch (e) { console.error(e); }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/roleplay/stats`, { credentials: 'include' });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchRecommendations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/roleplay/recommendations`, { credentials: 'include' });
      if (res.ok) setRecommendations(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchAchievements = async () => {
    try {
      const res = await fetch(`${API_URL}/api/roleplay/achievements`, { credentials: 'include' });
      if (res.ok) setAchievements(await res.json());
    } catch (e) { console.error(e); }
  };

  const startSession = async (mode = 'chat') => {
    if (!selectedPersona) {
      toast.error('Select a buyer persona first');
      return;
    }
    setStarting(true);
    try {
      const res = await fetch(`${API_URL}/api/roleplay/sessions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ persona_id: selectedPersona, difficulty: selectedDifficulty })
      });
      if (res.ok) {
        const data = await res.json();
        if (mode === 'voice') {
          navigate(`/practice/voice/${data.session_id}`);
        } else {
          navigate(`/practice/session/${data.session_id}`);
        }
      } else {
        toast.error('Failed to start session');
      }
    } catch (e) {
      toast.error('Failed to start session');
    } finally {
      setStarting(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getGradeColor = (grade) => {
    if (!grade) return 'text-muted-foreground';
    if (grade.startsWith('A')) return 'text-emerald-600';
    if (grade.startsWith('B')) return 'text-blue-600';
    if (grade.startsWith('C')) return 'text-amber-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="practice-page">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground flex items-center gap-3">
            <Swords className="w-7 h-7 text-primary" />
            Sales Roleplay Simulator
          </h1>
          <p className="text-muted-foreground mt-1">Practice sales conversations with AI-powered buyers</p>
        </div>

        {/* Stats Row */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Sessions</p>
                  <p className="font-heading text-2xl font-bold text-foreground">{stats?.total_sessions || 0}</p>
                </div>
                <Target className="w-8 h-8 text-primary/30" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Best Score</p>
                  <p className={`font-heading text-2xl font-bold ${getScoreColor(stats?.best_score || 0)}`}>{stats?.best_score || 0}</p>
                </div>
                <Trophy className="w-8 h-8 text-amber-600/30" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Average</p>
                  <p className={`font-heading text-2xl font-bold ${getScoreColor(stats?.average_score || 0)}`}>{stats?.average_score || 0}</p>
                </div>
                <Target className="w-8 h-8 text-muted-foreground/20" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Trend</p>
                  <div className="flex items-center gap-1">
                    <p className={`font-heading text-2xl font-bold ${(stats?.improvement_trend || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {(stats?.improvement_trend || 0) >= 0 ? '+' : ''}{stats?.improvement_trend || 0}
                    </p>
                  </div>
                </div>
                {(stats?.improvement_trend || 0) >= 0 ? (
                  <TrendingUp className="w-8 h-8 text-emerald-600/30" />
                ) : (
                  <TrendingDown className="w-8 h-8 text-red-600/30" />
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Achievements + Leaderboard Link */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Achievements */}
          {achievements && achievements.all?.length > 0 && (
            <Card className="bg-card border-border/50" data-testid="achievements-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-foreground">
                  <Award className="w-4 h-4 text-amber-600" />
                  Achievements
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {achievements.all.map(a => {
                    const earned = achievements.earned?.includes(a.id);
                    const iconMap = { play: Play, target: Target, flame: Flame, trophy: Trophy, star: Star, award: Award, crown: Crown, 'trending-up': TrendingUp };
                    const AchIcon = iconMap[a.icon] || Star;
                    return (
                      <div
                        key={a.id}
                        title={`${a.name}: ${a.description}`}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs transition-all ${
                          earned
                            ? 'bg-yellow-500/10 border-yellow-500/30 text-amber-600'
                            : 'bg-secondary/20 border-border/30 text-muted-foreground/40'
                        }`}
                      >
                        <AchIcon className="w-3.5 h-3.5" />
                        {a.name}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Leaderboard Link */}
          <Card className="bg-card border-border/50">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-sm text-foreground">Team Leaderboard</p>
                  <p className="text-xs text-muted-foreground">See how you rank against your team</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/leaderboard')} data-testid="leaderboard-link">
                View <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Personalized Training Recommendations */}
        {recommendations?.has_enough_data && recommendations.recommendations?.length > 0 && (
          <Card className="bg-card border-primary/20" data-testid="recommendations-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Brain className="w-5 h-5 text-primary" />
                Recommended Training
              </CardTitle>
              <p className="text-xs text-muted-foreground">Based on your {recommendations.sessions_analyzed} analyzed sessions</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Weak / Strong areas */}
              <div className="grid gap-3 md:grid-cols-2">
                <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/15">
                  <p className="text-xs font-medium text-red-600 mb-2">Areas to Improve</p>
                  {recommendations.weak_areas?.map((w, i) => (
                    <div key={`k1-${i}`} className="flex items-center justify-between mb-1">
                      <span className="text-sm text-foreground">{w.label}</span>
                      <span className="text-sm font-mono font-bold text-red-600">{w.avg_score}</span>
                    </div>
                  ))}
                </div>
                <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/15">
                  <p className="text-xs font-medium text-emerald-600 mb-2">Your Strengths</p>
                  {recommendations.strong_areas?.map((s, i) => (
                    <div key={`k2-${i}`} className="flex items-center justify-between mb-1">
                      <span className="text-sm text-foreground">{s.label}</span>
                      <span className="text-sm font-mono font-bold text-emerald-600">{s.avg_score}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recommended sessions */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Targeted Practice Sessions</p>
                {recommendations.recommendations.map((rec, i) => {
                  const Icon = PERSONA_ICONS[rec.persona_id] || UserCircle;
                  return (
                    <button
                      key={`k3-${i}`}
                      onClick={() => {
                        setSelectedPersona(rec.persona_id);
                        setSelectedDifficulty(rec.difficulty);
                        document.getElementById('start-section')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      data-testid={`rec-${i}`}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-all text-left"
                    >
                      <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{rec.persona_name}</span>
                          <Badge className={`${DIFFICULTY_COLORS[rec.difficulty]} border text-[10px]`}>{rec.difficulty}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">Focus: {rec.focus_skill} ({rec.current_avg}/100) — {rec.reason}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-primary shrink-0" />
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Start New Session */}
        <Card className="bg-card border-primary/20" id="start-section" data-testid="start-session-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Play className="w-5 h-5 text-primary" />
              Start New Session
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Persona Selection */}
            <div>
              <p className="text-sm font-medium text-foreground mb-3">Select Buyer Persona</p>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {personas.map(p => {
                  const Icon = PERSONA_ICONS[p.id] || UserCircle;
                  const isSelected = selectedPersona === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setSelectedPersona(p.id)}
                      data-testid={`persona-${p.id}`}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/10 ring-1 ring-primary/30'
                          : 'border-border/50 bg-secondary/20 hover:border-primary/30 hover:bg-secondary/40'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isSelected ? 'bg-primary/20' : 'bg-secondary'}`}>
                          <Icon className={`w-5 h-5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                        </div>
                        <div>
                          <p className="font-medium text-sm text-foreground">{p.name}</p>
                          <p className="text-xs text-muted-foreground">{p.job_title}</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{p.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Difficulty Selection */}
            <div>
              <p className="text-sm font-medium text-foreground mb-3">Select Difficulty</p>
              <div className="flex gap-3">
                {difficulties.map(d => (
                  <button
                    key={d.id}
                    onClick={() => setSelectedDifficulty(d.id)}
                    data-testid={`difficulty-${d.id}`}
                    className={`flex-1 p-3 rounded-xl border text-center transition-all ${
                      selectedDifficulty === d.id
                        ? 'border-primary bg-primary/10 ring-1 ring-primary/30'
                        : 'border-border/50 bg-secondary/20 hover:border-primary/30'
                    }`}
                  >
                    <p className="font-medium text-sm text-foreground">{d.label}</p>
                    <p className="text-xs text-muted-foreground mt-1">{d.description}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => startSession('chat')}
                disabled={!selectedPersona || starting}
                className="h-12 text-base"
                data-testid="start-session-btn"
              >
                {starting ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <MessageSquare className="w-5 h-5 mr-2" />
                )}
                {starting ? 'Starting...' : 'Text Chat'}
              </Button>
              <Button
                onClick={() => startSession('voice')}
                disabled={!selectedPersona || starting}
                variant="outline"
                className="h-12 text-base border-primary/30 hover:bg-primary/10"
                data-testid="start-voice-btn"
              >
                {starting ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Mic className="w-5 h-5 mr-2" />
                )}
                {starting ? 'Starting...' : 'Voice Call'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Past Sessions */}
        {sessions.length > 0 && (
          <Card className="bg-card border-border/50" data-testid="past-sessions">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-muted-foreground" />
                Past Sessions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {sessions.map(s => {
                  const persona = s.persona || {};
                  const Icon = PERSONA_ICONS[s.persona_id] || UserCircle;
                  return (
                    <button
                      key={s.session_id}
                      onClick={() => navigate(s.status === 'completed' ? `/practice/results/${s.session_id}` : `/practice/session/${s.session_id}`)}
                      data-testid={`session-${s.session_id}`}
                      className="w-full flex items-center justify-between p-3 rounded-lg bg-secondary/20 border border-border/40 hover:border-primary/30 hover:bg-secondary/40 transition-all text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center">
                          <Icon className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{persona.name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(s.created_at).toLocaleDateString()} &middot; {s.difficulty}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {s.status === 'completed' ? (
                          <>
                            <span className={`font-heading text-lg font-bold ${getScoreColor(s.overall_score || 0)}`}>
                              {s.overall_score || 0}
                            </span>
                            <Badge className={`${DIFFICULTY_COLORS[s.difficulty] || ''} border text-xs`}>
                              {s.grade || '--'}
                            </Badge>
                          </>
                        ) : (
                          <Badge className="bg-primary/15 text-primary border-primary/30 border text-xs">Active</Badge>
                        )}
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
